import { useProductDetails } from "../productContext/ProductProvider";
import productStyles from "../productDetails.module.css";
import styles from "./productAssets.module.css";
import { camilCaseToSentence, titleCase } from "../../../utils/Utils";
import { Download, FileOpen, ImageNotSupported, VideocamOff, Email } from "@mui/icons-material";
import { useCallback, useMemo, useRef, useState } from "react";
import { downloadAsset } from "../../assetPopUp/AssetPopup";
import useAsset from "../../../hooks/useAsset";
import PDFIcon from "../../../assets/img/icons/pdf.png";
import AIIcon from "../../../assets/img/icons/ai.png";
import ZIPIcon from "../../../assets/img/icons/zip.png";
const IGNORE_ASSETS = ["Compliance Assets", "Key Feature"];
import { useTranslation } from "react-i18next";
import { submitSelectedAssets } from "../../../service/productsService";
import { LinearProgress, Checkbox } from "@mui/material";
import SnackBar from "../../snackbarContent/SnackBar";
import { useMutation } from "@tanstack/react-query";
import EmailPopup from "../../emailPopUp/EmailPopup";
import { usePushToDataLayer } from "../../../hooks/usePushToDataLayer";
import { GTM_EVENTS } from "../../../utils/constants";

const CUSTOM_ORDER = {
	"Product Shot": 1,
	"Sample Image": 6,
	"POP Image": 7,
	"Video": 8,
	"Preview Image": 9,
	"Specification Sheet": Infinity,
};

const useSendAssetData = () => {
	return useMutation({
		mutationFn: async (item) => submitSelectedAssets({
			...item
		}),
	});
};

function ProductAssets() {
	const { t } = useTranslation();
	const { mutate: sendAssetData, isPending } = useSendAssetData();
	const pushToDL = usePushToDataLayer();
	const { derivedAssetType, assetData } = useProductDetails();
	const [assetSelected, setAssetSelected] = useState(() => new Set());
	const [popEmail, setPopEmail] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState('success');
	const [isProcessing, setIsProcessing] = useState(false);
	const [isItemSuccess, setItemsSuccess] = useState(false);

	const orderedKeys = useMemo(() => {
		if (!derivedAssetType) return null;
		const validKeys = Object.keys(derivedAssetType).filter((key) => {
			return Array.isArray(derivedAssetType[key]) && derivedAssetType[key].length > 0 && !IGNORE_ASSETS.includes(key);
		});

		return validKeys.sort((a, b) => {
			const orderA = CUSTOM_ORDER[a] || 1000;
			const orderB = CUSTOM_ORDER[b] || 1000;
			return orderA - orderB;
		});
	}, [derivedAssetType, IGNORE_ASSETS, CUSTOM_ORDER]);

	// Toggle individual checkbox asset
	const toggleAsset = useCallback((groupName, assetId) => {
		setAssetSelected((prev) => {
			const updated = new Set(prev);
			updated.has(assetId) ? updated.delete(assetId) : updated.add(assetId);

			const allSelected = derivedAssetType?.[groupName]?.every((id) => updated.has(id));
			allSelected ? updated.add(groupName) : updated.delete(groupName);

			return updated;
		});
	}, []);

	// Toggle group checkbox asset
	const toggleGroup = useCallback((groupName) => {
		setAssetSelected((prev) => {
			const updated = new Set(prev);
			const assetIds = derivedAssetType?.[groupName];
			const allSelected = assetIds?.every((id) => updated.has(id));

			assetIds?.forEach((id) => allSelected ? updated.delete(id) : updated.add(id));
			allSelected ? updated.add(groupName) : updated.delete(groupName);

			return updated;
		});
	}, []);

	const formatSelectedForSubmit = () => {
		return Object.entries(derivedAssetType)?.map(([groupName, ids]) => {
			const selectedIds = ids?.filter((id) => assetSelected.has(id));
			return {
				label: groupName,
				value: selectedIds,
			};
		}).filter((item) => item?.value?.length > 0);
	};

	const handleSend = useCallback((type, emails) => {
		const payload = formatSelectedForSubmit();

		if (payload?.length === 0) {
			setSnackbarOpen(true);
			setSnackbarMessage(t('Please select at least one Asset'));
			setSnackbarSeverity('warning');
			return;
		};

		if (type === 'email' && (!emails || emails?.length === 0) && !popEmail) {
			setItemsSuccess(false);
			setPopEmail(true);
			return;
		}

		setIsProcessing?.(true);
		setSnackbarMessage(t("Processing..."));
		setSnackbarSeverity("info");
		setSnackbarOpen(true);

		sendAssetData({
			type,
			payload,
			emails,
			isgrouped: false,
		}, {
			onSuccess: (data) => {
				if (type === 'download' && typeof data === 'string') {
					// Trigger automatic download
					const a = document.createElement('a');
					a.href = data;
					a.download = ''; // Optional - browser will name it from URL
					document.body.appendChild(a);
					a.click();
					document.body.removeChild(a);
					pushToDL(GTM_EVENTS.DOWNLOAD, {
						button_text: 'Download',
						asset_ids: payload.reduce((acc, val) => [...acc, ...val.value], []),
					})
				}

				let successMessage = t("Assets successfully downloaded");

				if (type === 'email') {
					setItemsSuccess(true);
					successMessage = t("Email sent successfully");
					setPopEmail(false);
					pushToDL(GTM_EVENTS.EMAIL_ME, {
						button_text: 'Send',
						asset_ids: payload.reduce((acc, val) => [...acc, ...val.value], []),
					})
				};

				setSnackbarOpen(true);
				setSnackbarMessage(successMessage);
				setSnackbarSeverity('success');
				setIsProcessing(false);
			},

			onError: (error) => {
				console.error(
					'Error in handleSend (ProductAssets)',
					error
				);
				setSnackbarMessage(t("Something went wrong. Try again Later"));
				setSnackbarSeverity("error");
				setSnackbarOpen(true);
				setIsProcessing(false);
				setItemsSuccess(false);
			}
		});
	}, [derivedAssetType, assetSelected]);

	const handleCloseSnackbar = () => {
		setSnackbarOpen(false);
		setSnackbarMessage('');
		setSnackbarSeverity('');
		setIsProcessing(false);
	};

	if (!derivedAssetType) {
		return <></>;
	}
	return (
		<>
			<section className={`${styles.productAssets} ${productStyles.section}`}>
				<div className={styles.dwnAllContainer}>
					<div className={`btn-group`}>
						<button
							onClick={() => handleSend('download')}
							disabled={isPending}
							className='btn btn-primary'>
							<div className='btn-icon'>
								<Download />
								{t("Download All")}
							</div>
						</button>
						<button
							onClick={() => handleSend('email')}
							disabled={isPending}
							className='btn btn-secondary'>
							<div className='btn-icon'>
								<Email />
								{t("Email All")}
							</div>
						</button>
					</div>
				</div>

				<div className={styles.titleContainer}>
					<div className={`${productStyles.title}`}>{t("Downloadable Assets")}</div>
				</div>

				<div className={styles.assets__container}>
					{orderedKeys &&
						orderedKeys.map((key) => {
							const title = key;
							const items = derivedAssetType[key];
							const itemObj = items.reduce((acc, itemId) => {
								if (!assetData?.[itemId] || !assetData?.[itemId]?.id) return acc;
								acc.push({
									itemId,
									assetData: assetData[itemId],
								});
								return acc;
							}, []);
							const allChildSelected = items?.every((id) => assetSelected.has(id));
							if (itemObj.length === 0) return null;
							return <AssetTypeSection key={title} title={title} isPending={isPending}
								allChildSelected={allChildSelected} toggleGroup={toggleGroup}
								toggleAsset={toggleAsset} assetSelected={assetSelected} items={itemObj} />;
						})}
				</div>
			</section>
			{
				popEmail && (
					<EmailPopup
						active={popEmail}
						setClose={setPopEmail}
						customStyles={{ position: "fixed" }}
						setSnackbarOpen={setSnackbarOpen}
						setSnackbarMessage={setSnackbarMessage}
						setSnackbarSeverity={setSnackbarSeverity}
						setIsProcessing={setIsProcessing}
						handleSend={handleSend}
						isItemsFetching={isPending}
						isItemSuccess={isItemSuccess}
						subTitle={
							t("To email the asset, please enter the recipient's email address(es) below.")
						}
					/>
				)
			}
			{
				snackbarOpen && (
					<SnackBar handleCloseSnackbar={handleCloseSnackbar}
						snackbar={{
							open: snackbarOpen,
							type: snackbarSeverity,
							message: snackbarMessage,
							processing: isProcessing,
						}}
						anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
					/>
				)
			}
		</>
	);
}

ProductAssets.shouldRender = ({ derivedAssetType, assetData }) => {
	return (
		assetData &&
		typeof assetData === "object" &&
		Object.keys(assetData).length !== 0 &&
		derivedAssetType &&
		typeof derivedAssetType === "object" &&
		Object.keys(derivedAssetType).length !== 0
	);
};
export default ProductAssets;

function AssetTypeSection({
	title, items, allChildSelected, isPending,
	toggleGroup, toggleAsset, assetSelected = new Set(),
}) {
	const { t } = useTranslation();
	const titleC = titleCase(camilCaseToSentence(title));

	return (
		<section className={styles.assetsTypeSubSection}>
			<h3 className={`${styles.subSectionTitleContainer} ${styles.subSectionTitle}`}>
				<Checkbox
					checked={allChildSelected}
					color="primary"
					onChange={() => toggleGroup(title)}
					disabled={isPending}
				/>
				{t(titleC)}
			</h3>
			<div className={styles.subSectionContent}>
				{items.map((item) => {
					const type = item.assetData?.mimeType;
					switch (type) {
						case "application/pdf":
							return <IconAssetItem key={item.itemId} item={item} alt={"pdf"} title={title}
								icon={PDFIcon} toggleAsset={toggleAsset} assetSelected={assetSelected} isPending={isPending} />;
						case "application/ai":
							return <IconAssetItem key={item.itemId} item={item} alt={"ai"} title={title}
								icon={AIIcon} toggleAsset={toggleAsset} assetSelected={assetSelected}
								customStyle={{ height: "90px", width: "auto" }} isPending={isPending} />;
						case "application/zip":
							return <IconAssetItem key={item.itemId} item={item} alt={"zip"} title={title}
								icon={ZIPIcon} toggleAsset={toggleAsset} assetSelected={assetSelected} isPending={isPending}/>;
						case "video/mp4":
						case "video/x-ms-wmv":
						case "video/quicktime":
							return <VideoAssetItem key={item.itemId} item={item} title={title}
								toggleAsset={toggleAsset} assetSelected={assetSelected} isPending={isPending} />;
						default:
							return <ImageAssetItem key={item.itemId} item={item} title={title}
								toggleAsset={toggleAsset} assetSelected={assetSelected} isPending={isPending}/>;
					}
				})}
			</div>
		</section>
	);
}

function ImageAssetItem({
	item, title, toggleAsset,
	assetSelected = new Set(), isPending,
}) {
	const { itemId, assetData } = item;
	const isImage = assetData?.mimeType.startsWith("image") || assetData?.extension === "eps";

	return (
		<div pimcore_id={itemId} className={styles.subSectionItem}>
			<Checkbox
				className={styles.assetCheckBox}
				color="primary"
				checked={assetSelected?.has?.(itemId) ?? false}
				onChange={() => toggleAsset(title, itemId)}
				disabled={isPending}
			/>
			{isImage ? (
				<img className={styles.subSectionItem__child} src={assetData.largeAssetPath} alt={assetData.fileName} />
			) : (
				<div className={styles.unSupportedType}>
					<ImageNotSupported sx={{ fontSize: 60 }} />
				</div>
			)}
			<HoverDetails id={itemId} />
		</div>
	);
}

export function IconAssetItem({
	item, alt, icon, title,
	toggleAsset, assetSelected = new Set(), isPending,
	customStyle = { height: "130px", width: "auto" },
}) {
	const { itemId } = item;
	return (
		<div pimcore_id={itemId} className={styles.subSectionItem}>
			<Checkbox
				className={styles.assetCheckBox}
				color="primary"
				checked={assetSelected?.has?.(itemId) ?? false}
				onChange={() => toggleAsset(title, itemId)}
				disabled={isPending}
			/>
			<div className={styles.unSupportedType}>
				<img src={icon} alt={alt} loading='eager' style={customStyle} />
			</div>
			<HoverDetails id={itemId} />
		</div>
	);
}	

function VideoAssetItem({
	item, title, toggleAsset,
	assetSelected = new Set(), isPending,
}) {
	const { itemId, assetData } = item;
	const isMp4 = assetData?.mimeType === "video/mp4";

	return (
		<div pimcore_id={itemId} className={styles.subSectionItem}>
			{/* <div className='video-wrapper'> */}
			<Checkbox
				className={styles.assetCheckBox}
				color="primary"
				checked={assetSelected?.has?.(itemId) ?? false}
				onChange={() => toggleAsset(title, itemId)}
				disabled={isPending}
			/>
			{isMp4 ? (
				<video className={styles.subSectionItem__child} preload='metadata' autoPlay playsInline muted loop loading='lazy'>
					<source src={assetData?.originalAssetPath} type={assetData?.mimeType}></source>
					<source src={assetData?.originalAssetPath} type='video/mp4'></source>
				</video>
			) : (
				<div className={styles.unSupportedType}>
					<VideocamOff sx={{ fontSize: 60 }} />
				</div>
			)}
			<HoverDetails id={itemId} />
		</div>
	);
}

function HoverDetails({ id }) {
	const {t}=useTranslation()
	const [isDownloading, setIsDownloading] = useState(false)
	const [snackbar, setSnackbar] = useState({ open: false, message: '', type: '', errorType: "tryAndCatch" });
	const { setAssetPopUpId } = useProductDetails();
	const { data } = useAsset(id);
	const downloadAnchor = useRef();
	const handleCloseSnackbar = () => setSnackbar({ open: false, message: '', type: '' });
	return (
		<div className={styles.onHoverInfo}>
			<a className='anchorForDownload' ref={downloadAnchor}></a>
			<div className={styles.btns}>
				<div className={`btn-group column`} style={{ alignItems: "center" }}>
					<button
						className='btn btn-primary'
						onClick={() => {
							downloadAsset(data, downloadAnchor, setIsDownloading, setSnackbar);
						}}
						disabled={isDownloading}>
						<div className='btn-icon'>
							<Download />
							{t("Download")}
						</div>
						{isDownloading && <LinearProgress />}
					</button>
					<button
						className='btn btn-secondary'
						onClick={() => {
							setAssetPopUpId(id);
						}}>
						<div className='btn-icon'>
							<FileOpen />
							{t("View Details")}
						</div>
					</button>
				</div>
			</div>
			<SnackBar handleCloseSnackbar={handleCloseSnackbar} snackbar={snackbar} />
		</div>
	);
}
